const DEFAULT_BREAKPOINT = 768;
const DEFAULT_TYPE_SPEED = 45;
const DEFAULT_DELETE_SPEED = 24;
const DEFAULT_PAUSE = 1800;

export function normalizePlaceholderList(value, fallback = '') {
    const values = Array.isArray(value)
        ? value
        : typeof value === 'string'
            ? value.split(/\r?\n/)
            : [];

    const normalized = values
        .flatMap((item) => typeof item === 'string' ? item.split(/\r?\n/) : [])
        .map((item) => item.trim())
        .filter(Boolean);

    if (normalized.length) return normalized;

    const fallbackValue = typeof fallback === 'string' ? fallback.trim() : '';
    return fallbackValue ? [fallbackValue] : [];
}

export function selectPlaceholderTexts(config = {}, viewportWidth) {
    const fallback = typeof config.fallback === 'string' ? config.fallback : '';
    const desktop = normalizePlaceholderList(config.examples, fallback);
    const mobile = normalizePlaceholderList(config.mobileExamples, '');
    const breakpoint = Number(config.mobileBreakpoint) > 0
        ? Number(config.mobileBreakpoint)
        : DEFAULT_BREAKPOINT;
    const width = Number.isFinite(Number(viewportWidth))
        ? Number(viewportWidth)
        : (typeof window !== 'undefined' ? window.innerWidth : Number.POSITIVE_INFINITY);

    return width <= breakpoint && mobile.length ? mobile : desktop;
}

function getViewportWidth() {
    return typeof window !== 'undefined' ? window.innerWidth : Number.POSITIVE_INFINITY;
}

export function createPlaceholderRotator(field, config = {}) {
    if (!field) {
        return {
            start() {},
            stop() {},
            destroy() {},
            isRunning: () => false,
        };
    }

    const fallback = typeof config.fallback === 'string'
        ? config.fallback.trim()
        : (field.placeholder || '');
    const typeSpeed = Math.max(10, Number(config.typeSpeed) || DEFAULT_TYPE_SPEED);
    const deleteSpeed = Math.max(10, Number(config.deleteSpeed) || DEFAULT_DELETE_SPEED);
    const pause = Math.max(250, Number(config.pause) || DEFAULT_PAUSE);
    const originalPlaceholder = field.placeholder || fallback;
    let timer = null;
    let running = false;
    let destroyed = false;
    let phase = 'typing';
    let textIndex = 0;
    let characterIndex = 0;

    const isAllowedToAnimate = () => {
        const activeElement = typeof document !== 'undefined' ? document.activeElement : null;
        return !destroyed && !field.disabled && !field.value && activeElement !== field;
    };

    const clearTimer = () => {
        if (timer !== null) {
            clearTimeout(timer);
            timer = null;
        }
    };

    const stop = (restore = true) => {
        clearTimer();
        running = false;
        if (restore && !field.value && !field.disabled) {
            field.placeholder = originalPlaceholder;
        }
    };

    const schedule = (callback, delay) => {
        timer = setTimeout(() => {
            timer = null;
            callback();
        }, delay);
    };

    const tick = () => {
        if (!running || !isAllowedToAnimate()) {
            stop(false);
            return;
        }

        const texts = selectPlaceholderTexts({ ...config, fallback }, getViewportWidth());
        if (!texts.length) {
            stop(true);
            return;
        }

        if (textIndex >= texts.length) textIndex = 0;
        const text = texts[textIndex];

        if (phase === 'typing') {
            characterIndex += 1;
            field.placeholder = text.slice(0, characterIndex);
            if (characterIndex >= text.length) {
                phase = 'pause';
                schedule(tick, pause);
            } else {
                schedule(tick, typeSpeed);
            }
            return;
        }

        if (phase === 'pause') {
            phase = 'deleting';
            schedule(tick, deleteSpeed);
            return;
        }

        characterIndex -= 1;
        field.placeholder = text.slice(0, Math.max(0, characterIndex));
        if (characterIndex <= 0) {
            textIndex += 1;
            phase = 'typing';
            schedule(tick, typeSpeed);
        } else {
            schedule(tick, deleteSpeed);
        }
    };

    const start = () => {
        if (running || !isAllowedToAnimate()) return;
        running = true;
        phase = 'typing';
        characterIndex = 0;
        tick();
    };

    const onInput = () => stop(!field.value);
    const onFocus = () => stop(true);
    const onBlur = () => {
        if (!field.value) setTimeout(start, 0);
    };
    const onResize = () => {
        if (!field.value && !field.disabled && typeof document !== 'undefined' && document.activeElement !== field) {
            stop(true);
            start();
        }
    };

    field.addEventListener?.('input', onInput);
    field.addEventListener?.('focus', onFocus);
    field.addEventListener?.('blur', onBlur);
    if (typeof window !== 'undefined') {
        window.addEventListener?.('resize', onResize);
    }

    start();

    return {
        start,
        stop,
        destroy() {
            destroyed = true;
            stop(false);
            field.removeEventListener?.('input', onInput);
            field.removeEventListener?.('focus', onFocus);
            field.removeEventListener?.('blur', onBlur);
            if (typeof window !== 'undefined') {
                window.removeEventListener?.('resize', onResize);
            }
        },
        isRunning: () => running,
    };
}

